import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import Mention from '@tiptap/extension-mention'
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion'
import type { MentionNodeAttrs } from '@tiptap/extension-mention'
import { useMembers } from '@/lib/hooks/useWorkspaceUsers'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

interface MentionUser {
  id: string
  name: string
  email?: string
  profilePictureUrl?: string | null
}

// Store for workspace users - populated by MentionUsersProvider
let workspaceUsers: Array<MentionUser> = []

export function setMentionUsers(users: Array<MentionUser>) {
  workspaceUsers = users
}

function MentionsMenu({
  items,
  selectedIndex,
  onSelect,
  onHover,
}: {
  items: Array<MentionUser>
  selectedIndex: number
  onSelect: (index: number) => void
  onHover: (index: number) => void
}) {
  return (
    <div
      className={cn(
        'z-50 min-w-[280px] max-w-[320px] overflow-hidden rounded-lg border border-border bg-popover shadow-lg',
        'animate-in fade-in-0 zoom-in-95',
      )}
    >
      {items.length === 0 ? (
        <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
          No users found
        </div>
      ) : (
        <ul className="max-h-[300px] overflow-y-auto p-1">
          {items.map((user, index) => {
            const parts = user.name.split(' ')
            const initials =
              parts.length >= 2
                ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
                : user.name.substring(0, 2).toUpperCase()

            return (
              <li
                key={user.id}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  selectedIndex === index
                    ? 'bg-secondary text-foreground'
                    : 'text-foreground hover:bg-accent',
                )}
                onMouseEnter={() => onHover(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(index)}
              >
                <Avatar className="h-7 w-7">
                  {user.profilePictureUrl && (
                    <AvatarImage src={user.profilePictureUrl} alt={user.name} />
                  )}
                  <AvatarFallback className="bg-muted text-xs font-medium text-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-1 flex-col overflow-hidden">
                  <span className="truncate font-medium">
                    {user.name}
                  </span>
                  {user.email && (
                    <span className="truncate text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function MentionMenuWrapper(props: SuggestionProps<MentionUser>) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const handleSelect = (index: number) => {
    const user = props.items[index]
    if (user) {
      props.command({ id: user.id, label: user.name } as MentionNodeAttrs)
    }
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowUp') {
      setSelectedIndex((prev) =>
        prev <= 0 ? props.items.length - 1 : prev - 1,
      )
      return true
    }
    if (event.key === 'ArrowDown') {
      setSelectedIndex((prev) =>
        prev >= props.items.length - 1 ? 0 : prev + 1,
      )
      return true
    }
    if (event.key === 'Enter') {
      handleSelect(selectedIndex)
      return true
    }
    return false
  }

  const wrapperRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = wrapperRef.current
    if (el) {
      ;(el as any).__onKeyDown = onKeyDown
    }
  })

  useLayoutEffect(() => {
    setSelectedIndex(0)
  }, [props.items])

  if (props.items.length === 0) return null

  return (
    <div ref={wrapperRef}>
      <MentionsMenu
        items={props.items}
        selectedIndex={selectedIndex}
        onSelect={handleSelect}
        onHover={setSelectedIndex}
      />
    </div>
  )
}

const mentionSuggestion: Omit<SuggestionOptions<MentionUser>, 'editor'> = {
  char: '@',
  allowSpaces: false,
  items: ({ query }) => {
    const lower = query.toLowerCase()
    return workspaceUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(lower) ||
        (user.email && user.email.toLowerCase().includes(lower)),
    )
  },
  render: () => {
    let container: HTMLDivElement | null = null
    let root: ReturnType<typeof createRoot> | null = null

    const positionContainer = (rect: DOMRect | null) => {
      if (!container || !rect) return
      const margin = 8
      const menuWidth = container.offsetWidth || 280
      const menuHeight = container.offsetHeight || 300
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let left = rect.left + window.scrollX
      const maxLeft = window.scrollX + viewportWidth - menuWidth - margin
      const minLeft = window.scrollX + margin
      if (left > maxLeft) left = maxLeft
      if (left < minLeft) left = minLeft

      let top = rect.bottom + window.scrollY
      if (rect.bottom + menuHeight + margin > viewportHeight) {
        top = rect.top + window.scrollY - menuHeight
      }

      container.style.left = `${left}px`
      container.style.top = `${top}px`
    }

    return {
      onStart: (props) => {
        container = document.createElement('div')
        container.style.position = 'absolute'
        container.style.zIndex = '100'

        document.body.appendChild(container)
        root = createRoot(container)
        root.render(<MentionMenuWrapper {...props} />)

        if (props.clientRect) {
          requestAnimationFrame(() => positionContainer(props.clientRect?.() ?? null))
        }
      },
      onUpdate: (props) => {
        root?.render(<MentionMenuWrapper {...props} />)
        if (props.clientRect) {
          requestAnimationFrame(() => positionContainer(props.clientRect?.() ?? null))
        }
      },
      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          root?.unmount()
          container?.remove()
          container = null
          root = null
          return true
        }
        const el = container?.firstElementChild
        if (el && (el as any).__onKeyDown) {
          return (el as any).__onKeyDown(props.event)
        }
        return false
      },
      onExit: () => {
        root?.unmount()
        container?.remove()
        container = null
        root = null
      },
    }
  },
}

export const MentionExtension = Mention.configure({
  HTMLAttributes: {
    class:
      'inline-flex items-center rounded-full bg-secondary px-2 py-px text-[0.9em] font-medium text-foreground hover:bg-accent transition-colors cursor-pointer',
  },
  suggestion: mentionSuggestion,
  renderLabel: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
})

/**
 * Hook to sync workspace users into the mention extension's user store.
 * Call this in any component that renders TiptapRichTextEditor with mentions enabled.
 */
export function useMentionUsers() {
  const { users } = useMembers()

  useMemo(() => {
    setMentionUsers(
      users.map((user) => ({
        id: user.id || '',
        name: user.name || user.email || 'Unknown User',
        email: user.email ?? undefined,
        profilePictureUrl: user.profilePictureUrl,
      })),
    )
  }, [users])
}

/**
 * Extract mentioned user IDs from Tiptap JSON content.
 * Traverses the document tree to find mention nodes.
 */
export function extractMentionsFromContent(
  tiptapJson: string,
): Array<string> {
  try {
    const doc = JSON.parse(tiptapJson)
    const mentionedUserIds: Array<string> = []

    function traverseNodes(node: any) {
      if (!node) return

      if (node.type === 'mention' && node.attrs?.id) {
        mentionedUserIds.push(node.attrs.id)
      }

      if (Array.isArray(node.content)) {
        node.content.forEach(traverseNodes)
      }
    }

    traverseNodes(doc)
    return Array.from(new Set(mentionedUserIds))
  } catch {
    return []
  }
}
